import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTracking, useTrackingReply } from '@/lib/requests'
import { STATUS_CSS_COLOR_FOR_REQUEST } from '@/lib/taskVisuals'

export function TrackPage() {
  const { t, i18n } = useTranslation()
  const { publicId } = useParams<{ publicId: string }>()
  const { data, isLoading } = useTracking(publicId ?? '')
  const reply = useTrackingReply(publicId ?? '')
  const [message, setMessage] = useState('')

  if (isLoading) return <div className="flex min-h-screen items-center justify-center text-sm text-ink-soft">…</div>

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4 text-center">
        <p className="text-sm text-ink-soft">{t('track.notFound')}</p>
      </div>
    )
  }

  const { request, messages } = data

  return (
    <div className="mx-auto min-h-screen max-w-lg px-4 py-10">
      <div className="mb-6 flex items-center gap-2.5">
        <img src="/brand/tar-group-logo.png" alt="TAR Group" className="h-6 w-auto" />
        <span className="font-display text-sm font-semibold tracking-wide">TAR Ops</span>
      </div>

      <div className="rounded-card border border-line bg-surface p-4">
        <div className="flex items-center gap-2">
          <span
            className="h-2 w-2 flex-none rounded-full"
            style={{ background: STATUS_CSS_COLOR_FOR_REQUEST[request.status] }}
            aria-hidden="true"
          />
          <p className="font-display text-lg font-semibold">{t(`track.status.${request.status}`)}</p>
        </div>
        <p className="mt-1 text-xs text-ink-soft">
          {t('track.submittedOn', { date: new Date(request.createdAt).toLocaleDateString(i18n.resolvedLanguage) })}
        </p>
      </div>

      <div className="mt-4 flex flex-col gap-2.5">
        {messages.map((m) => (
          <div
            key={m.id}
            className={`max-w-[85%] rounded-card border p-3 text-sm ${
              m.author === 'requester' ? 'ms-auto border-brand/30 bg-brand/5' : 'border-line bg-surface'
            }`}
          >
            <p>{m.bodyMd}</p>
            <p className="mt-1 text-[11px] text-ink-soft">{new Date(m.createdAt).toLocaleString(i18n.resolvedLanguage)}</p>
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-col gap-2">
        <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">{t('track.reply')}</label>
        <textarea
          rows={3}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t('track.replyPlaceholder')}
          className="w-full rounded-md border-2 border-ink-soft/25 bg-surface-2 px-3 py-2 text-sm outline-none placeholder:text-ink-soft/60 focus:border-brand"
        />
        <button
          type="button"
          disabled={!message.trim() || reply.isPending}
          onClick={() => {
            reply.mutate(message, { onSuccess: () => setMessage('') })
          }}
          className="self-end rounded-md bg-brand px-4 py-2 text-sm font-semibold text-brand-ink disabled:opacity-60"
        >
          {t('track.send')}
        </button>
      </div>
    </div>
  )
}
